import { useCallback, useEffect } from "react";
import { Box, Container, Typography, useTheme } from "@mui/material";
import { GlobalHeader, useDrawerContext } from "@WahooFitness/wahoo-offline-mfe";
import { t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { WfSubscriptionValueList } from "@WahooFitness/redesignr";
import useTieredMembership from "./useTieredMembership";
import PaywallContainer from "@/components/Paywall/PaywallContainer";
import SubscriptionCard from "./SubscriptionCard";
import SubscriptionDebugInfo from "./SubscriptionDebugInfo";
import useSubscriptionDebug from "./useSubscriptionDebug";

function TieredMembership() {
  const { debugOpen, handleTierTap, handleDebugClose } = useSubscriptionDebug();
  const { setDrawer, handleClose: handleDrawerClose } = useDrawerContext();

  const { subscriptionData, userSubscription, customerInfo } = useTieredMembership();

  const { palette, zIndex } = useTheme();
  const { i18n } = useLingui();

  const closeDebugPanel = useCallback(() => {
    handleDrawerClose();
    handleDebugClose();
  }, [handleDrawerClose, handleDebugClose]);

  useEffect(() => {
    if (!debugOpen) return;
    setDrawer({
      open: true,
      isPersistent: true,
      hideHeader: true,
      hidePuller: true,
      maxHeight: "70vh",
      children: (
        <SubscriptionDebugInfo
          userSubscription={userSubscription}
          customerInfo={customerInfo}
          onClose={closeDebugPanel}
        />
      ),
    });
  }, [debugOpen, userSubscription, customerInfo, closeDebugPanel, setDrawer]);

  // Close the drawer if the user navigates away while the debug panel is open.
  useEffect(() => {
    return () => {
      if (debugOpen) handleDrawerClose();
    };
  }, [debugOpen, handleDrawerClose]);

  // Invisible fixed overlay over the header title area — tapping it 10x opens the debug panel.
  const debugTapTarget = (
    <Box
      id="debug-tap-target"
      position="fixed"
      top={0}
      left="30%"
      right="30%"
      height={56}
      zIndex={zIndex.appBar + 1}
      onClick={handleTierTap}
      sx={{ userSelect: "none" }}
    />
  );

  if (!subscriptionData) {
    return (
      <>
        {debugTapTarget}
        <PaywallContainer closeViewId="membership" />
      </>
    );
  }

  return (
    <>
      <GlobalHeader title={t`My subscription`} />
      {debugTapTarget}
      <Box>
        <Box sx={{ backgroundColor: palette.background.level1, paddingBottom: 3 }}>
          <Container maxWidth="sm" disableGutters>
            <Box gap={2} display="flex" flexDirection="column" alignItems="center" p={2}>
              <SubscriptionCard subscriptionData={subscriptionData} />
            </Box>
            {subscriptionData?.description && (
              <Box pt={2} px={2} pb={1.5} maxWidth="sm" mx="auto" width="100%">
                {subscriptionData.description}
              </Box>
            )}
            {subscriptionData.tier && (
              <WfSubscriptionValueList
                tier={subscriptionData.trialing ? "pro" : subscriptionData.tier}
                canceled={subscriptionData.canceled}
                trialing={subscriptionData.trialing}
                i18nContext={i18n}
              />
            )}
          </Container>
        </Box>

        {subscriptionData?.action && (
          <Box p={2} gap={2} display="flex" flexDirection="column" maxWidth="sm" mx="auto">
            <Typography variant="ui-lg-bold">
              <Trans>Manage subscription</Trans>
            </Typography>
            {subscriptionData?.action}
          </Box>
        )}
      </Box>
    </>
  );
}

export default TieredMembership;
